import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useApp } from "@/contexts/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build per-item assignment rows from a scanned bill.
// Each row: { name, amount, is_tax, paidBy, participants }
const buildItemRows = (scannedBill, defaultMemberId, allMemberIds) => {
  const items = scannedBill?.items;
  if (!items || !items.length) return null;
  return items.map((it, i) => ({
    name: it.name || (it.is_tax ? "Tax" : `Item ${i + 1}`),
    amount: Number(it.amount || 0),
    is_tax: !!it.is_tax,
    paidBy: defaultMemberId || "",
    participants: [...allMemberIds],
  }));
};

const AddExpenseModal = ({ group, scannedBill, onSuccess, onClose }) => {
  const { t, formatAmount } = useApp();
  const memberIds = group.members.map(m => m.id);
  const firstMember = group.members[0]?.id || '';

  // Per-item assignment mode (scanned bill with items)
  const itemRows = useMemo(
    () => buildItemRows(scannedBill, firstMember, memberIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [items, setItems] = useState(itemRows || []);
  const isItemMode = items.length > 0;

  // Manual / single-expense mode (no items)
  const [description, setDescription] = useState(scannedBill?.title || '');
  const [amount, setAmount] = useState(scannedBill?.total?.toString() || '');
  const [paidBy, setPaidBy] = useState(firstMember);
  const [participants, setParticipants] = useState(memberIds);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  const toggleParticipant = (id) => {
    setParticipants(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const setItem = (idx, patch) => {
    setItems(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const toggleItemParticipant = (idx, id) => {
    setItems(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const has = r.participants.includes(id);
      return { ...r, participants: has ? r.participants.filter(x => x !== id) : [...r.participants, id] };
    }));
  };

  const perPerson = participants.length > 0 && amount
    ? parseFloat(amount) / participants.length
    : 0;

  const saveItem = async (row) => {
    if (!row.paidBy || row.participants.length === 0 || row.amount <= 0) return false;
    await axios.post(`${API}/groups/${group.id}/expenses`, {
      description: row.name,
      amount: row.amount,
      paid_by: row.paidBy,
      participants: row.participants,
      date
    });
    return true;
  };

  const handleSaveItems = async () => {
    const valid = items.filter(r => r.paidBy && r.participants.length > 0 && r.amount > 0);
    if (!valid.length) {
      toast.error(t('error_fill_required'));
      return;
    }
    setSaving(true);
    try {
      // ponytail: sequential POSTs — parallel would race on sqlite writes; add batching when item count grows.
      for (const row of valid) await saveItem(row);
      toast.success(t('settlement_recorded'));
      onSuccess();
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSingle = async () => {
    if (!description.trim() || !amount || !paidBy || participants.length === 0) {
      toast.error(t('error_fill_required'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/groups/${group.id}/expenses`, {
        description: description.trim(),
        amount: parseFloat(amount),
        paid_by: paidBy,
        participants,
        date
      });
      toast.success(t('settlement_recorded'));
      onSuccess();
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = isItemMode ? handleSaveItems : handleSaveSingle;

  const totalAssigned = items.reduce((s, r) => s + r.amount, 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={isItemMode ? "max-w-lg" : "max-w-md"} data-testid="add-expense-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">{t('add_expense')}</DialogTitle>
        </DialogHeader>

        {scannedBill && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{t('scanned_from_bill')}</p>
            <p className="text-sm text-emerald-700 mt-0.5">{scannedBill.title} · {formatAmount(scannedBill.total)}</p>
          </div>
        )}

        {isItemMode ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('assign_items')}</Label>
              <p className="text-xs text-zinc-400 mt-0.5">{items.length} {t('items_count')} · {formatAmount(scannedBill.total)}</p>
            </div>

            <div className="max-h-[42vh] overflow-y-auto space-y-3 pr-1">
              {items.map((row, idx) => {
                const share = row.participants.length > 0 ? row.amount / row.participants.length : 0;
                return (
                  <div key={idx} className="border border-zinc-200 rounded-xl p-3 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {row.is_tax && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium shrink-0">
                            {t('tax')}
                          </span>
                        )}
                        <span className="text-sm font-medium text-zinc-900 truncate">{row.name}</span>
                      </div>
                      <span className="text-sm font-bold text-zinc-900 ml-2 shrink-0">{formatAmount(row.amount)}</span>
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wide mb-1">{t('payer')}</p>
                        <Select value={row.paidBy} onValueChange={v => setItem(idx, { paidBy: v })}>
                          <SelectTrigger className="h-9"><SelectValue placeholder={t('select_payer')} /></SelectTrigger>
                          <SelectContent>
                            {group.members.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wide mb-1">
                          {t('assign_to')}{share > 0 && <span className="text-zinc-300 normal-case ml-1">· {formatAmount(share)}</span>}
                        </p>
                        <div className="space-y-1.5">
                          {group.members.map(m => (
                            <div key={m.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={row.participants.includes(m.id)}
                                onCheckedChange={() => toggleItemParticipant(idx, m.id)}
                                id={`it${idx}-${m.id}`}
                              />
                              <label htmlFor={`it${idx}-${m.id}`} className="text-xs text-zinc-700 cursor-pointer">{m.name}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-zinc-200">
              <span className="text-xs text-zinc-400">{t('total')}: {formatAmount(totalAssigned)}</span>
            </div>

            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('date')}</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1.5 h-11" />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {scannedBill && !items.length && (
              <p className="text-xs text-zinc-500">{t('no_items')}</p>
            )}
            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('description')}</Label>
              <Input
                data-testid="expense-description-input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t('expense_description_placeholder')}
                className="mt-1.5 h-11"
              />
            </div>

            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('amount')} ({group.currency})</Label>
              <Input
                data-testid="expense-amount-input"
                type="number"
                min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0"
                className="mt-1.5 h-11"
              />
            </div>

            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('paid_by')}</Label>
              <Select value={paidBy} onValueChange={setPaidBy}>
                <SelectTrigger data-testid="expense-paid-by-select" className="mt-1.5 h-11">
                  <SelectValue placeholder={t('paid_by')} />
                </SelectTrigger>
                <SelectContent>
                  {group.members.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('split_among')}</Label>
              <div className="mt-2 space-y-2">
                {group.members.map(m => (
                  <div key={m.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        id={`p-${m.id}`}
                        checked={participants.includes(m.id)}
                        onCheckedChange={() => toggleParticipant(m.id)}
                      />
                      <label htmlFor={`p-${m.id}`} className="text-sm text-zinc-700 cursor-pointer">{m.name}</label>
                    </div>
                    {participants.includes(m.id) && perPerson > 0 && (
                      <span className="text-xs text-zinc-400">{formatAmount(perPerson)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{t('date')}</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1.5 h-11" />
            </div>
          </div>
        )}

        <DialogFooter className="mt-2 gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-200">{t('cancel')}</Button>
          <Button
            data-testid="save-expense-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
          >
            {saving ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddExpenseModal;